import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker } from "@devhop/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { format, subDays } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

function findDateConditions(filters: FilterConditionDTO[] | undefined): {
  from?: string;
  to?: string;
} {
  if (!filters) return {};
  let from: string | undefined;
  let to: string | undefined;
  for (const item of filters) {
    if (item.op === "and" && Array.isArray(item.value)) {
      for (const c of item.value as FilterConditionDTO[]) {
        if (c.field === "requestedAt" && typeof c.value === "string") {
          if (c.op === "gte") from = c.value.slice(0, 10);
          if (c.op === "lte") to = c.value.slice(0, 10);
        }
      }
    }
  }
  return { from, to };
}

const STATUS_OPTIONS = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "requested", label: "ລໍຖ້າກວດສອບ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "rejected", label: "ປະຕິເສດ" },
  { value: "refunded", label: "ຄືນເງິນແລ້ວ" },
  { value: "closed", label: "ປິດແລ້ວ" },
];

const TARGET_TYPE_OPTIONS = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "package", label: "ແພັກເກດຄລາດສ໌" },
  { value: "private_lesson_package", label: "ແພັກເກດຮຽນສ່ວນຕົວ" },
];

export type RefundReportSearch = {
  filters?: FilterConditionDTO[];
};

export function RefundReportFilter() {
  const nav = useNavigate({ from: "/app/reports/refund" });
  const search = useSearch({
    from: "/app/reports/refund",
  }) as RefundReportSearch;

  const filters = search.filters ?? [];
  const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const defaultTo = format(new Date(), "yyyy-MM-dd");
  const dateRange = findDateConditions(filters);

  const statusFilter = findCondition(filters, "status");
  const targetTypeFilter = findCondition(filters, "targetType");

  const [fromDate, setFromDate] = useState(dateRange.from ?? defaultFrom);
  const [toDate, setToDate] = useState(dateRange.to ?? defaultTo);

  useEffect(() => {
    setFromDate(dateRange.from ?? defaultFrom);
    setToDate(dateRange.to ?? defaultTo);
  }, [dateRange.from, dateRange.to]);

  const statusValue =
    statusFilter?.op === "eq" && typeof statusFilter?.value === "string"
      ? statusFilter.value
      : "all";
  const targetTypeValue =
    targetTypeFilter?.op === "eq" && typeof targetTypeFilter?.value === "string"
      ? targetTypeFilter.value
      : "all";

  const hasFilter =
    statusValue !== "all" ||
    targetTypeValue !== "all" ||
    !!dateRange.from ||
    !!dateRange.to;

  const applyFilters = (updates: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    targetType?: string;
  }) => {
    let nextFilters: FilterConditionDTO[] = [...filters];

    if (updates.status !== undefined) {
      nextFilters =
        updates.status === "all"
          ? (removeConditions(nextFilters, "status") ?? [])
          : upsertCondition(nextFilters, {
              field: "status",
              op: "eq",
              value: updates.status,
            });
    }
    if (updates.targetType !== undefined) {
      nextFilters =
        updates.targetType === "all"
          ? (removeConditions(nextFilters, "targetType") ?? [])
          : upsertCondition(nextFilters, {
              field: "targetType",
              op: "eq",
              value: updates.targetType,
            });
    }
    if (updates.dateFrom !== undefined || updates.dateTo !== undefined) {
      const fromStr = updates.dateFrom ?? fromDate;
      const toStr = updates.dateTo ?? toDate;
      nextFilters = removeConditions(nextFilters, "requestedAt") ?? [];
      const dateConditions: FilterConditionDTO[] = [];
      if (fromStr) {
        dateConditions.push({
          field: "requestedAt",
          op: "gte",
          value: `${fromStr} 00:00:00`,
        });
      }
      if (toStr) {
        dateConditions.push({
          field: "requestedAt",
          op: "lte",
          value: `${toStr} 23:59:59`,
        });
      }
      if (dateConditions.length > 0) {
        nextFilters = upsertCondition(nextFilters, {
          field: "requestedAt",
          op: "and",
          value: dateConditions,
        } as FilterConditionDTO);
      }
    }

    nav({ search: { filters: nextFilters.length ? nextFilters : undefined } });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? new Date(fromDate) : undefined,
              to: toDate ? new Date(toDate) : undefined,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : (range as { from?: Date; to?: Date })?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : (range as { from?: Date; to?: Date })?.to;
              const fromStr = from ? format(from, "yyyy-MM-dd") : defaultFrom;
              const toStr = to ? format(to, "yyyy-MM-dd") : defaultTo;
              setFromDate(fromStr);
              setToDate(toStr);
              applyFilters({ dateFrom: fromStr, dateTo: toStr });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />

          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => applyFilters({ status: val })}
            placeholder="ສະຖານະ"
            className="!h-8 w-full sm:w-40"
            options={STATUS_OPTIONS}
          />

          <SimpleSelect
            value={targetTypeValue}
            onValueChange={(val) => applyFilters({ targetType: val })}
            placeholder="ປະເພດເປົ້າໝາຍ"
            className="!h-8 w-full sm:w-44"
            options={TARGET_TYPE_OPTIONS}
          />
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/app/refunds"
            search={{ filters: filters.length ? filters : undefined }}
            className="text-muted-foreground text-sm underline hover:text-foreground"
          >
            ໄປຫນ້າຄຳຂໍຄືນເງິນ
          </Link>
          {hasFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFromDate(defaultFrom);
                setToDate(defaultTo);
                applyFilters({
                  dateFrom: defaultFrom,
                  dateTo: defaultTo,
                  status: "all",
                  targetType: "all",
                });
              }}
              className="max-sm:w-full"
            >
              <XIcon className="size-4" />
              ລ້າງ
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
